import { apiRequest } from './axiosConfig';
import {
  Product,
  ProductsResponse,
  ProductResponse,
  ProductMutationResponse,
  CreateProductDto,
  UpdateProductDto,
  BrandGuideline,
  FetchProgress,
  ProductExtendedMetadata,
  ProductFilters,
  BulkDeleteResponse,
  ImageUploadResponse
} from '../../types/product';

class ProductService {
  /**
   * Product list with filtering and pagination
   * @param filters Filter criteria for products
   * @returns Paginated product list
   */
  async getProducts(filters?: ProductFilters): Promise<ProductsResponse> {
    try {
      console.log('Product取得を開始します...', filters);

      const config = {
        timeout: 30000,
        params: filters || {}
      };

      const response = await apiRequest.get<ProductsResponse>('products', config);
      console.log('===== Product API応答 =====');
      console.log('API応答ステータス: 成功');
      console.log(`取得された製品数: ${response.data.length}`);

      return response;
    } catch (error: any) {
      console.error('Product一覧取得に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Get single product by ID
   * @param productId Product ID
   * @returns Product details with related products
   */
  async getProductById(productId: number): Promise<ProductResponse> {
    try {
      console.log(`Product(ID: ${productId})取得を開始します...`);

      const config = { timeout: 30000 };
      const response = await apiRequest.get<ProductResponse>(`products/${productId}`, config);

      console.log('===== Product詳細API応答 =====');
      console.log('API応答ステータス: 成功');
      console.log(`関連製品数: ${response.related_products?.length || 0}`);

      return response;
    } catch (error: any) {
      console.error(`Product(ID: ${productId})の取得に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Product list from the Knowledge product API.
   */
  async getKnowledgeProducts(filters?: Pick<ProductFilters, 'search' | 'category' | 'status'>): Promise<{ data: Product[] }> {
    try {
      const config = {
        timeout: 30000,
        params: filters || {}
      };

      return await apiRequest.get<{ data: Product[] }>('knowledge/products', config);
    } catch (error: any) {
      console.error('Knowledge Product一覧取得に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Get a single product from the Knowledge product API.
   */
  async getKnowledgeProductById(productId: number): Promise<ProductMutationResponse> {
    try {
      const config = { timeout: 30000 };
      return await apiRequest.get<ProductMutationResponse>(
        `knowledge/products/${productId}`,
        config
      );
    } catch (error: any) {
      console.error(`Knowledge Product(ID: ${productId})の取得に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Create new product
   * @param data Product creation data
   * @returns Created product
   */
  async createProduct(data: CreateProductDto): Promise<ProductMutationResponse> {
    try {
      console.log('Product作成を開始します...', data.name);

      // If images are File objects, we need to use FormData
      const hasFileImages = data.images?.some(img => img instanceof File);

      if (hasFileImages) {
        const formData = new FormData();

        // Add basic fields
        Object.entries(data).forEach(([key, value]) => {
          if (key === 'images') return; // Handle images separately
          if (key === 'tags' || key === 'metadata') {
            formData.append(key, JSON.stringify(value));
          } else if (value !== undefined && value !== null) {
            formData.append(key, String(value));
          }
        });

        // Add image files
        if (data.images) {
          data.images.forEach((image, index) => {
            if (image instanceof File) {
              formData.append(`images[${index}]`, image);
            }
          });
        }

        const config = {
          timeout: 60000, // Longer timeout for file uploads
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        };

        return await apiRequest.post<ProductMutationResponse>('products', formData, config);
      } else {
        // Standard JSON request
        const config = { timeout: 30000 };
        return await apiRequest.post<ProductMutationResponse>('products', data, config);
      }
    } catch (error: any) {
      console.error('Product作成に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Update existing product
   * @param productId Product ID
   * @param data Product update data
   * @returns Updated product
   */
  async updateProduct(productId: number, data: UpdateProductDto): Promise<ProductMutationResponse> {
    try {
      console.log(`Product(ID: ${productId})更新を開始します...`);

      // If images are File objects, we need to use FormData
      const hasFileImages = data.images?.some(img => img instanceof File);

      if (hasFileImages) {
        const formData = new FormData();

        // Add _method for Laravel method spoofing
        formData.append('_method', 'PUT');

        // Add basic fields
        Object.entries(data).forEach(([key, value]) => {
          if (key === 'images') return; // Handle images separately
          if (key === 'tags' || key === 'metadata') {
            formData.append(key, JSON.stringify(value));
          } else if (value !== undefined && value !== null) {
            formData.append(key, String(value));
          }
        });

        // Add image files
        if (data.images) {
          data.images.forEach((image, index) => {
            if (image instanceof File) {
              formData.append(`images[${index}]`, image);
            }
          });
        }

        const config = {
          timeout: 60000, // Longer timeout for file uploads
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        };

        return await apiRequest.post<ProductMutationResponse>(`products/${productId}`, formData, config);
      } else {
        // Standard JSON request
        const config = { timeout: 30000 };
        return await apiRequest.put<ProductMutationResponse>(`products/${productId}`, data, config);
      }
    } catch (error: any) {
      console.error(`Product(ID: ${productId})の更新に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Delete product
   * @param productId Product ID
   * @returns Success message
   */
  async deleteProduct(productId: number): Promise<{ message: string }> {
    try {
      console.log(`Product(ID: ${productId})削除を開始します...`);

      const config = { timeout: 30000 };
      const response = await apiRequest.delete<{ message: string }>(`products/${productId}`, config);

      console.log('Product削除が完了しました');
      return response;
    } catch (error: any) {
      console.error(`Product(ID: ${productId})の削除に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Delete product through the Knowledge product API.
   */
  async deleteKnowledgeProduct(productId: number): Promise<{ message: string }> {
    try {
      const config = { timeout: 30000 };
      return await apiRequest.delete<{ message: string }>(`knowledge/products/${productId}`, config);
    } catch (error: any) {
      console.error(`Knowledge Product(ID: ${productId})の削除に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Update brand guideline stored in product extended metadata
   * @param productId Product ID
   * @param guideline Brand guideline payload
   * @returns Updated product
   */
  async updateBrandGuideline(productId: number, guideline: BrandGuideline): Promise<ProductMutationResponse> {
    try {
      const config = { timeout: 30000 };
      const response = await apiRequest.put<ProductMutationResponse>(
        `knowledge/products/${productId}/brand-guideline`,
        { brand_guideline: guideline },
        config
      );

      return response;
    } catch (error: any) {
      console.error(`Product(ID: ${productId})ブランドガイドライン更新に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Regenerate ONLY the brand guideline from the product URL.
   * 商品情報全体を作り直す再取得（refetch）と違い、他のメタデータには触らない。
   * @param productId Product ID
   * @returns Generated brand guideline
   */
  async generateBrandGuideline(productId: number): Promise<{ message: string; brand_guideline: BrandGuideline }> {
    try {
      // URL解析とAI呼び出しを含むため、通常のミューテーションより長めに待つ。
      const config = { timeout: 120000 };
      return await apiRequest.post<{ message: string; brand_guideline: BrandGuideline }>(
        `knowledge/products/${productId}/brand-guideline/generate`,
        {},
        config
      );
    } catch (error: any) {
      console.error(`Product(ID: ${productId})ブランドガイドライン生成に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Update product data through the Knowledge product API.
   */
  async updateKnowledgeProduct(productId: number, data: {
    name?: string;
    url?: string | null;
    description?: string;
    status?: 'active' | 'inactive' | 'discontinued';
    category_ids?: number[];
    tag_ids?: number[];
    extended_metadata?: ProductExtendedMetadata;
  }): Promise<ProductMutationResponse> {
    try {
      const config = { timeout: 30000 };
      return await apiRequest.put<ProductMutationResponse>(
        `knowledge/products/${productId}`,
        data,
        config
      );
    } catch (error: any) {
      console.error(`Knowledge Product(ID: ${productId})更新に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Get URL extraction progress for a Knowledge product.
   */
  async getKnowledgeFetchProgress(productId: number): Promise<{
    fetch_progress: FetchProgress | null;
    extended_metadata?: ProductExtendedMetadata;
  }> {
    try {
      const config = { timeout: 30000 };
      return await apiRequest.get<{
        fetch_progress: FetchProgress | null;
        extended_metadata?: ProductExtendedMetadata;
      }>(`knowledge/products/${productId}/fetch-progress`, config);
    } catch (error: any) {
      console.error(`Knowledge Product(ID: ${productId})取得進捗の取得に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Get competitors linked to a Knowledge product.
   */
  async getKnowledgeCompetitors(productId: number): Promise<any[]> {
    try {
      const config = { timeout: 30000 };
      const response = await apiRequest.get<{ data: any[] }>(
        `knowledge/products/${productId}/competitors`,
        config
      );

      return response.data || [];
    } catch (error: any) {
      console.error(`Knowledge Product(ID: ${productId})競合取得に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Restore deleted product
   * @param productId Product ID
   * @returns Restored product
   */
  async restoreProduct(productId: number): Promise<ProductMutationResponse> {
    try {
      console.log(`Product(ID: ${productId})復元を開始します...`);

      const config = { timeout: 30000 };
      const response = await apiRequest.post<ProductMutationResponse>(`products/${productId}/restore`, {}, config);

      console.log('Product復元が完了しました');
      return response;
    } catch (error: any) {
      console.error(`Product(ID: ${productId})の復元に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Bulk delete products
   * @param productIds Array of product IDs to delete
   * @returns Deletion result
   */
  async bulkDeleteProducts(productIds: number[]): Promise<BulkDeleteResponse> {
    try {
      console.log(`Productの一括削除を開始します... (${productIds.length}件)`);

      const config = { timeout: 30000 };
      const response = await apiRequest.delete<BulkDeleteResponse>('products/bulk', {
        ...config,
        data: { ids: productIds }
      });

      console.log(`Product一括削除が完了しました (${response.deleted_count}件)`);
      return response;
    } catch (error: any) {
      console.error('Product一括削除に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Upload images for product
   * @param productId Product ID
   * @param images Array of image files
   * @returns Updated product with uploaded images
   */
  async uploadProductImages(productId: number, images: File[]): Promise<ImageUploadResponse> {
    try {
      console.log(`Product(ID: ${productId})画像アップロードを開始します... (${images.length}枚)`);

      const formData = new FormData();
      images.forEach((image, index) => {
        formData.append(`images[${index}]`, image);
      });

      const config = {
        timeout: 60000, // Longer timeout for file uploads
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      const response = await apiRequest.post<ImageUploadResponse>(
        `products/${productId}/images`,
        formData,
        config
      );

      console.log(`画像アップロードが完了しました (${response.uploaded_images.length}枚)`);
      return response;
    } catch (error: any) {
      console.error(`Product(ID: ${productId})の画像アップロードに失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Delete product image
   * @param productId Product ID
   * @param imageId Image ID to delete
   * @returns Updated product
   */
  async deleteProductImage(productId: number, imageId: string): Promise<ProductMutationResponse> {
    try {
      console.log(`Product(ID: ${productId})画像(ID: ${imageId})削除を開始します...`);

      const config = { timeout: 30000 };
      const response = await apiRequest.delete<ProductMutationResponse>(
        `products/${productId}/images`,
        {
          ...config,
          data: { image_id: imageId }
        }
      );

      console.log('画像削除が完了しました');
      return response;
    } catch (error: any) {
      console.error(`Product画像削除に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Create product from URL (auto-fetch info with GPT-5)
   * @param data URL registration data
   * @returns Created product (status will be 'fetching')
   */
  async createFromUrl(data: {
    name: string;
    description: string;
    url: string;
    tag_ids?: number[];
    category_id?: number | null;
    // false のとき Deep Research を遅延（画像・競合の選択後に startProductResearch で開始）
    start_fetch?: boolean;
  }): Promise<ProductMutationResponse> {
    try {
      console.log('URLからProduct作成を開始します...', data.url);

      const config = { timeout: 30000 };
      const response = await apiRequest.post<ProductMutationResponse>(
        'knowledge/products/url-register',
        data,
        config
      );

      console.log('Product登録が完了しました');
      return response;
    } catch (error: any) {
      console.error('URLからのProduct作成に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * URLから競合候補を取得（保存はしない）。登録ウィザードの競合選択で使用。
   */
  async discoverCompetitorsFromUrl(url: string, title?: string, category?: string): Promise<Array<{
    name: string;
    company_url?: string | null;
    description?: string | null;
    strengths?: string | null;
    weaknesses?: string | null;
  }>> {
    try {
      const config = { timeout: 120000 };
      const response = await apiRequest.post<{ data: any[] }>(
        'knowledge/products/discover-competitors-from-url',
        { url, title, category },
        config
      );
      return response.data || [];
    } catch (error: any) {
      console.error('競合候補の取得に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * 遅延していた Deep Research（情報取得ジョブ）を開始する。既存の refetch エンドポイントを再利用。
   */
  async startProductResearch(productId: number): Promise<void> {
    const config = { timeout: 30000 };
    await apiRequest.put(`knowledge/products/${productId}/refetch`, {}, config);
  }

  /**
   * Create a knowledge product without URL extraction.
   * Used when the user registers a product manually from the knowledge modal.
   */
  async createKnowledgeProduct(data: {
    name: string;
    url?: string | null;
    description?: string;
    status?: 'active' | 'inactive' | 'discontinued';
    category_ids?: number[];
    tag_ids?: number[];
    extended_metadata?: ProductExtendedMetadata;
  }): Promise<ProductMutationResponse> {
    try {
      const config = { timeout: 30000 };
      const response = await apiRequest.post<ProductMutationResponse>(
        'knowledge/products',
        {
          status: 'active',
          description: '',
          ...data,
        },
        config
      );

      return response;
    } catch (error: any) {
      console.error('Knowledge Product作成に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Create a competitor linked to a Knowledge product.
   */
  async createKnowledgeCompetitor(productId: number, data: {
    name: string;
    // Knowledge\CompetitorController@store は company_url を保存する（website_url は受け取らない）。
    company_url?: string | null;
    description?: string;
    tags?: string[];
  }): Promise<any> {
    try {
      const config = { timeout: 30000 };
      const response = await apiRequest.post<{ data: any }>(
        `knowledge/products/${productId}/competitors`,
        data,
        config
      );

      return response.data;
    } catch (error: any) {
      console.error(`Knowledge Product(ID: ${productId})競合作成に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Search products with advanced filters
   * @param query Search query string
   * @param filters Additional filter criteria
   * @returns Filtered product list
   */
  async searchProducts(query: string, filters?: Partial<ProductFilters>): Promise<ProductsResponse> {
    try {
      console.log(`Product検索を開始します: "${query}"`);

      const searchFilters: ProductFilters = {
        search: query,
        ...filters
      };

      return await this.getProducts(searchFilters);
    } catch (error: any) {
      console.error('Product検索に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Get products by category
   * @param category Category name
   * @param filters Additional filter criteria
   * @returns Filtered product list
   */
  async getProductsByCategory(category: string, filters?: Partial<ProductFilters>): Promise<ProductsResponse> {
    try {
      console.log(`カテゴリ別Product取得を開始します: "${category}"`);

      const categoryFilters: ProductFilters = {
        category,
        ...filters
      };

      return await this.getProducts(categoryFilters);
    } catch (error: any) {
      console.error('カテゴリ別Product取得に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Get products by status
   * @param status Product status
   * @param filters Additional filter criteria
   * @returns Filtered product list
   */
  async getProductsByStatus(status: 'active' | 'inactive' | 'discontinued', filters?: Partial<ProductFilters>): Promise<ProductsResponse> {
    try {
      console.log(`ステータス別Product取得を開始します: "${status}"`);

      const statusFilters: ProductFilters = {
        status,
        ...filters
      };

      return await this.getProducts(statusFilters);
    } catch (error: any) {
      console.error('ステータス別Product取得に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }
}

export default new ProductService();
